import { useTranslation } from 'react-i18next';
import { Box, Center, HStack, SimpleGrid, Spinner, Text } from '@chakra-ui/react';
import { StatCard } from '@themes/aegix';
import {
  HiUserGroup,
  HiShieldCheck,
  HiCheckCircle,
  HiIdentification,
  HiClipboardDocumentCheck,
  HiAcademicCap,
  HiClock,
} from 'react-icons/hi2';
import { useEventMetrics } from '../../hooks/queries/useReunification';

interface EventMetricsProps {
  eventId: string;
}

export function EventMetrics({ eventId }: EventMetricsProps) {
  const { t } = useTranslation();
  const { data: metrics, isLoading, error } = useEventMetrics(eventId);

  if (isLoading) {
    return (
      <Center py={12}>
        <Spinner size="lg" color="button.primary" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center py={12}>
        <Text color="text.danger">
          {error instanceof Error ? error.message : t('events.failedLoadMetrics', 'Failed to load metrics')}
        </Text>
      </Center>
    );
  }

  if (!metrics) {
    return (
      <Center py={12}>
        <Text color="text.secondary">{t('events.noMetricsAvailable', 'No metrics available')}</Text>
      </Center>
    );
  }

  const releasePct =
    metrics.total_students > 0
      ? Math.round((metrics.students_released / metrics.total_students) * 100)
      : 0;

  const elapsedMinutes = metrics.event_duration_minutes;
  const elapsedDisplay =
    elapsedMinutes === null
      ? '--'
      : elapsedMinutes >= 60
        ? `${Math.floor(elapsedMinutes / 60)}h ${elapsedMinutes % 60}m`
        : `${elapsedMinutes}m`;

  return (
    <Box>
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={4} mb={4}>
        <StatCard
          label={t('events.totalStudents', 'Total Students')}
          value={metrics.total_students}
          icon={HiUserGroup}
          colorPalette="purple"
          data-e2e="metrics-total-students"
        />
        <StatCard
          label={t('reunification.accounted', 'Accounted')}
          value={metrics.students_accounted}
          icon={HiCheckCircle}
          colorPalette="blue"
          data-e2e="metrics-accounted"
        />
        <StatCard
          label={t('reunification.released', 'Released')}
          value={metrics.students_released}
          icon={HiShieldCheck}
          colorPalette="green"
          subtext={t('events.pctOfTotal', '{{pct}}% of total', { pct: releasePct })}
          data-e2e="metrics-released"
        />
        <StatCard
          label={t('reunification.guardiansArrived', 'Guardians Arrived')}
          value={metrics.guardians_arrived}
          icon={HiIdentification}
          colorPalette="teal"
          data-e2e="metrics-guardians-arrived"
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={4} mb={4}>
        <StatCard
          label={t('reunification.guardiansVerified', 'Guardians Verified')}
          value={metrics.guardians_verified}
          icon={HiClipboardDocumentCheck}
          colorPalette="cyan"
          data-e2e="metrics-guardians-verified"
        />
        <StatCard
          label={t('reunification.staffActive', 'Staff Active')}
          value={metrics.staff_checked_in}
          icon={HiAcademicCap}
          colorPalette="orange"
          data-e2e="metrics-staff-active"
        />
        <StatCard
          label={t('events.avgReleaseTime', 'Avg Release Time')}
          value={
            metrics.avg_time_to_release_minutes !== null
              ? `${metrics.avg_time_to_release_minutes} min`
              : '--'
          }
          icon={HiClock}
          colorPalette="yellow"
          data-e2e="metrics-avg-release-time"
        />
        <StatCard
          label={t('events.elapsedTime', 'Elapsed Time')}
          value={elapsedDisplay}
          icon={HiClock}
          colorPalette="gray"
          data-e2e="metrics-elapsed-time"
        />
      </SimpleGrid>

      <Box
        bg="surface.primary"
        border="1px solid"
        borderColor="border.secondary"
        borderRadius="lg"
        p={4}
        data-e2e="metrics-release-progress-box"
      >
        <HStack justify="space-between" mb={2}>
          <Text fontSize="sm" fontWeight="600" color="text.primary">
            {t('reunification.releaseProgress', 'Release Progress')}
          </Text>
          <Text fontSize="sm" color="text.secondary" data-e2e="metrics-release-progress-text">
            {metrics.students_released} / {metrics.total_students} ({releasePct}%)
          </Text>
        </HStack>
        <Box
          h="8px"
          bg="gray.200"
          borderRadius="full"
          overflow="hidden"
        >
          <Box
            h="100%"
            w={`${releasePct}%`}
            bg="green.500"
            borderRadius="full"
            transition="width 0.3s ease"
            data-e2e="metrics-release-progress-bar"
          />
        </Box>
      </Box>
    </Box>
  );
}
